import { paymentStatusLabel, paymentStatusOptions } from "helpers/paymentLifecycle";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { gameCenterLimits } from "configs/limits";
import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";

import { useAppSelector, useAppDispatch } from "store/index";
import {
  handleWithdrawalsPaginate,
  getWithdrawalsList,
  getWithdrawalStatusesList,
} from "store/players/list/slice";

import { NotificationTypes, showNotification } from "store/notifications/slice";

import Pagination from "components/Pagination";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";
import EmptyState from "components/EmptyState/EmptyState";
import { formatDate, formatTime, useTimeFormat } from "helpers/timeFormat";


type WithdrawalsTableProps = {
  canUpdate: boolean;
};

const WithdrawalsTable: React.FC<WithdrawalsTableProps> = ({ canUpdate }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const timeFormat = useTimeFormat();

  const withdrawalsList = useAppSelector(
    (store) => store.players.withdrawalsList,
  );

  const totalItems = useAppSelector(
    (store) => store.players.totalItemsWithdrawals,
  );

  const totalPages = useAppSelector(
    (store) => store.players.totalPagesWithdrawals,
  );

  const activePage = useAppSelector(
    (store) => store.players.activePageWithdrawals,
  );

  const statusesList = useAppSelector(
    (store) => store.players.withdrawalStatusesList,
  );

  const isFetched = useAppSelector(
    (store) => store.players.isWithdrawalsFetched,
  );

  const [limit, setLimit] = useState(gameCenterLimits[0]);



  useEffect(() => {
    const userId = searchParams.get("entityId");
    if (!userId) return;

    dispatch(
      getWithdrawalsList({
        id: Number(userId),
        limit,
        offset: activePage - 1,
      }),
    );

    dispatch(getWithdrawalStatusesList());
  }, [dispatch, activePage, limit, searchParams]);

  const handleChangeStatus = async (id: number, status: string) => {
    if (!canUpdate) return;

    try {
      await instance.put(`${apiRoutes.paymentWithdrawalModifyStatus}/${id}`, {
        statusCode: status,
        reason: "manual_change_from_player_card",
      });

      dispatch(
        showNotification({
          title: t("success"),
          message: t("statusChanged"),
          type: NotificationTypes.success,
        }),
      );

      const userId = searchParams.get("entityId");
      if (!userId) return;

      dispatch(
        getWithdrawalsList({
          id: Number(userId),
          limit,
          offset: activePage - 1,
        }),
      );
    } catch {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("statusChangeError"),
          type: NotificationTypes.error,
        }),
      );
    }
  };

  const handlePrevClick = () => {
    if (activePage > 1) {
      dispatch(handleWithdrawalsPaginate(activePage - 1));
    }
  };

  const handleNextClick = () => {
    if (activePage !== totalPages) {
      dispatch(handleWithdrawalsPaginate(activePage + 1));
    }
  };

  return (
    <div className="player-content-wrapper">
      {!isFetched ? (
        <ModalTabSkeleton />
      ) : withdrawalsList.length === 0 ? (
        <EmptyState message={t("noPlayerWithdrawalsFound")} />
      ) : (
        <>
          <div className="inner-table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>{t("amount")}</th>
                  <th>{t("fee")}</th>
                  <th>{t("currency")}</th>
                  <th>{t("method")}</th>
                  <th>{t("provider")}</th>
                  <th>{t("status")}</th>
                  <th>{t("created")}</th>
                </tr>
              </thead>

              <tbody>
                {withdrawalsList.map((row) => {
                  const statusLabel = row.canonical_status;
                  const statusOptions = paymentStatusOptions(row, statusesList);
                  const isDisabled = !row.allowed_status_actions?.length;

                  return (
                    <tr key={row.id}>
                      <td>{row.id} {row.origin === "backoffice_manual" && <span>{t("manual")}</span>}</td>
                      <td>{row.amount}</td>
                      <td>{row.fee}</td>
                      <td>{row.currency}</td>
                      <td>{row.paymentMethod}</td>

                      <td>
                        {row.paymentProviderImage && (
                          <img
                            className="finance-provider-logo"
                            src={row.paymentProviderImage}
                            alt=""
                            onError={(event) => {
                              event.currentTarget.style.display = "none";
                            }}
                          />
                        )}
                      </td>

                      <td>
                        <div
                          className={`table-status-dropdown table-status-dropdown--withdrawals table-status-dropdown--${statusLabel}`}
                        >
                          <DefaultDropdown
                            value={row.canonical_status}
                            selectedLabel={paymentStatusLabel(row, statusesList)}
                            options={statusOptions}
                            isSmall
                            disabled={!canUpdate || isDisabled}
                            onSelect={(value) => {
                              if (!row.allowed_status_actions?.includes(String(value))) return;
                              handleChangeStatus(row.id, value as string);
                            }}
                          />
                        </div>
                      </td>

                      <td className="date">
                        <span>{formatDate(row.created)}</span>
                        <span>{formatTime(row.created, timeFormat)}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {withdrawalsList.length > 0 && (
            <Pagination
              limit={limit}
              activePage={activePage}
              totalItems={totalItems}
              totalPages={totalPages}
              limitsArray={gameCenterLimits}
              handleLimitClick={(nextLimit) => {
                setLimit(nextLimit);
                dispatch(handleWithdrawalsPaginate(1));
              }}
              handleNextClick={handleNextClick}
              handlePrevClick={handlePrevClick}
            />
          )}
        </>
      )}
    </div>
  );
};

export default WithdrawalsTable;
